import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { FormTemplate, FormField } from '../types/formBuilder';
import { TemplateService } from '../services/templateService';
import { FormSubmissionService } from '../services/formSubmissionService';
import LoadingScreen from '../components/common/LoadingScreen';
import { storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const SimpleCustomerFormFiller: React.FC = () => {
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (templateId) {
      loadTemplate();
    }
  }, [templateId]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const loadedTemplate = await TemplateService.getFormTemplate(templateId);
      
      if (!loadedTemplate) {
        setError(t('formFiller.templateNotFound') || 'Form template not found');
        return;
      }

      // Check if form is available on customer portal
      if (!loadedTemplate.customerPortalSettings?.isAvailableOnPortal) {
        setError(t('formFiller.notAvailableOnPortal') || 'This form is not available on the customer portal');
        return;
      }

      // Check if customer has access
      if (!canCustomerAccessForm(loadedTemplate)) {
        setError(t('formFiller.noAccess') || 'You do not have access to this form');
        return;
      }

      setTemplate(loadedTemplate);
      
      // Initialize form data with default values
      const initialData: Record<string, any> = {};
      loadedTemplate.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          initialData[field.id] = field.defaultValue;
        }
      });
      setFormData(initialData);
    } catch (error) {
      console.error('Error loading template:', error);
      setError(t('formFiller.loadError') || 'Error loading form template');
    } finally {
      setIsLoading(false);
    }
  };

  const canCustomerAccessForm = (template: FormTemplate): boolean => {
    if (!user || user.role !== 'customer') return false;
    
    const settings = template.customerPortalSettings;
    if (!settings) return false;

    // If allowed for all customers
    if (settings.allowAllCustomers) return true;

    // If specific customers allowed
    if (settings.allowedCustomers && user.customerId) {
      return settings.allowedCustomers.includes(user.customerId);
    }

    return false;
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const validateForm = (): boolean => {
    if (!template) return false;

    // Checkbox/Switch validáció - value === true ellenőrzés
    for (const field of template.fields) {
      if (field.required && (field.type === 'checkbox' || field.type === 'switch')) {
        const value = formData[field.id];
        if (value !== true) {
          const errorMessage = field.validations?.find(v => v.rule === 'required')?.message || 
                              (field.type === 'switch' 
                                ? (t('formBuilder.validation.switchRequired') || 'This switch must be enabled')
                                : (t('formBuilder.validation.checkboxRequired') || 'This checkbox must be checked'));
          toast.error(`${field.label}: ${errorMessage}`);
          return false;
        }
      }
    }
    return true;
  };

  const uploadFile = async (file: File, fieldId: string): Promise<string> => {
    const timestamp = Date.now();
    const fileName = `${timestamp}_${file.name}`;
    const storagePath = `formSubmissions/${templateId}/${fieldId}/${fileName}`;
    const storageRef = ref(storage, storagePath);
    
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!template || !user) return;

    // Validáció futtatása
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitterName = user.displayName || 
                             user.email?.split('@')[0] || 
                             `Customer ${user.uid.slice(-6)}`;

      // Process form data and upload files
      const processedData: Record<string, any> = {};
      
      for (const [fieldId, value] of Object.entries(formData)) {
        const field = template.fields.find(f => f.id === fieldId);
        
        // Handle file uploads
        if (field && (field.type === 'file' || field.type === 'image')) {
          if (value instanceof File) {
            // Single file
            toast.loading('Fájl feltöltése...', { id: 'file-upload' });
            processedData[fieldId] = await uploadFile(value, fieldId);
            toast.dismiss('file-upload');
          } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
            // Multiple files
            toast.loading(`${value.length} fájl feltöltése...`, { id: 'file-upload' });
            const uploadedUrls = await Promise.all(
              value.map((file: File) => uploadFile(file, fieldId))
            );
            processedData[fieldId] = uploadedUrls;
            toast.dismiss('file-upload');
          } else {
            processedData[fieldId] = value;
          }
        } else {
          processedData[fieldId] = value;
        }
      }

      const submissionData = {
        templateId: template.id,
        templateName: template.name,
        organizationId: user.organizationId,
        submittedBy: user.id,
        submittedByName: submitterName,
        data: processedData,
        status: 'submitted' as const
      };

      await FormSubmissionService.createSubmission(submissionData);

      setIsSubmitted(true);
      toast.success(t('formFiller.submitted') || 'Form submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('formFiller.submitError') || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/customer-portal');
  };

  const renderField = (field: FormField) => {
    const value = formData[field.id] || '';
    const baseInputClasses = "w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-colors";

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <input
            type={field.type}
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            className={baseInputClasses}
          />
        );

      case 'phone':
        return (
          <input
            type="tel"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            className={baseInputClasses}
          />
        );

      case 'number':
      case 'currency':
      case 'percentage':
        return (
          <input
            type="number"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            min={field.config?.min}
            max={field.config?.max}
            step={field.config?.step}
            className={baseInputClasses}
          />
        );

      case 'textarea':
        return (
          <textarea
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            placeholder={field.placeholder}
            rows={4}
            className={baseInputClasses}
          />
        );

      case 'select':
        return (
          <select
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            className={baseInputClasses}
          >
            <option value="">Select an option</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-start">
            <input
              type="checkbox"
              id={field.id}
              checked={value || false}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              required={field.required}
              className="h-4 w-4 mt-0.5 text-primary-600 focus:ring-primary-500 border border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor={field.id} className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              {field.label}
            </label>
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option.value} className="flex items-center">
                <input
                  type="radio"
                  id={`${field.id}-${option.value}`}
                  name={field.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                  required={field.required}
                  disabled={option.disabled}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border border-gray-300 dark:border-gray-600"
                />
                <label htmlFor={`${field.id}-${option.value}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        );

      case 'date':
        return (
          <input
            type="date"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            min={field.config?.minDate}
            max={field.config?.maxDate}
            className={baseInputClasses}
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            className={baseInputClasses}
          />
        );

      case 'time':
        return (
          <input
            type="time"
            id={field.id}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            className={baseInputClasses}
          />
        );

      case 'switch':
        return (
          <div className="flex items-center mt-2">
            <label htmlFor={field.id} className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id={field.id}
                checked={value || false}
                onChange={(e) => handleFieldChange(field.id, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-gray-600 peer-checked:bg-primary-600"></div>
            </label>
          </div>
        );

      case 'multiselect':
        return (
          <select
            id={field.id}
            value={value}
            onChange={(e) => {
              const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
              handleFieldChange(field.id, selectedOptions);
            }}
            required={field.required}
            multiple
            className={baseInputClasses + " min-h-[120px]"}
          >
            {field.options?.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'file':
      case 'image':
        return (
          <div className="space-y-2">
            <input
              type="file"
              id={field.id}
              onChange={(e) => {
                const files = e.target.files;
                if (files) {
                  if (field.config?.multiple) {
                    handleFieldChange(field.id, Array.from(files));
                  } else {
                    handleFieldChange(field.id, files[0]);
                  }
                }
              }}
              required={field.required}
              multiple={field.config?.multiple || false}
              accept={field.config?.accept || (field.type === 'image' ? 'image/*' : '*/*')}
              className="block w-full text-sm text-gray-900 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 file:mr-4 file:py-2 file:px-4 file:rounded-l-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 dark:file:bg-primary-900 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100 dark:hover:file:bg-primary-800"
            />
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>
                {field.config?.multiple ? 'Több fájl engedélyezett' : 'Egyetlen fájl'}
                {field.config?.maxSize && ` • Max ${Math.round(field.config.maxSize / (1024 * 1024))}MB`}
              </span>
              {field.config?.accept && field.config.accept !== '*/*' && (
                <span className="text-primary-600 dark:text-primary-400">
                  {field.config.accept}
                </span>
              )}
            </div>
            {value && (
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {Array.isArray(value) 
                  ? `${value.length} fájl kiválasztva` 
                  : value.name || 'Fájl kiválasztva'}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            Field type "{field.type}" is not yet supported in customer portal
          </div>
        );
    }
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-red-100 dark:border-gray-700 p-8 text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
            <ExclamationTriangleIcon className="h-10 w-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('formFiller.errorTitle') || 'Unable to Load Form'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
            {error}
          </p>
          <button
            onClick={handleCancel}
            className="w-full px-4 py-3 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2 inline" />
            {t('common.back') || 'Back to Portal'}
          </button>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-xl border border-green-100 dark:border-gray-700 p-8 text-center">
          <div className="relative mx-auto mb-6">
            <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
              <CheckCircleIcon className="h-10 w-10 text-white" />
            </div>
            <div className="absolute inset-0 rounded-full border-4 border-green-300 animate-ping"></div>
          </div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-3">
            {t('formFiller.submitted') || 'Form Submitted Successfully!'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
            {t('formFiller.submitSuccessMessage') || 'Thank you for your submission. Your response has been recorded.'}
          </p>
          <button
            onClick={handleCancel}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg"
          >
            {t('common.backToPortal') || 'Back to Portal'}
          </button>
        </div>
      </div>
    );
  }

  if (!template) {
    return null;
  }

  // Determine container width based on form layout
  const hasWideFields = template.fields.some(field => 
    field.width === 'half' || field.width === 'third' || field.width === 'quarter'
  );
  const containerMaxWidth = hasWideFields ? 'max-w-7xl' : 'max-w-3xl';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className={`${containerMaxWidth} mx-auto px-3 sm:px-4 py-4 sm:py-6`}>
        <div className="bg-white dark:bg-gray-800 rounded-lg sm:rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-4 sm:px-6 py-4 sm:py-6 text-white">
            <h1 className="text-xl sm:text-2xl font-bold mb-1">{template.name}</h1>
            {template.description && (
              <p className="text-sm sm:text-base text-primary-100">{template.description}</p>
            )}
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
            {/* Render sections if available */}
            {template.sections && template.sections.length > 0 ? (
              template.sections.map((section, sectionIndex) => {
                const sectionFields = template.fields.filter(f => f.section === section.id);
                if (sectionFields.length === 0) return null;
                
                return (
                  <div key={section.id} className="space-y-4">
                    {/* Section Header */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-1 w-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"></div>
                      <div>
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {section.label}
                        </h3>
                        {section.description && (
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                            {section.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Section Fields */}
                    <div className="flex flex-wrap -mx-2">
                      {sectionFields.map((field) => {
                        const widthClass = field.width === 'half' ? 'md:w-1/2' : 
                                         field.width === 'third' ? 'md:w-1/3' : 
                                         field.width === 'quarter' ? 'md:w-1/4' : 'w-full';
                        return (
                        <div key={field.id} className={`${widthClass} px-2 mb-4 space-y-2`}>
                          {field.type !== 'checkbox' && field.type !== 'switch' && (
                            <label htmlFor={field.id} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                          )}
                          {field.type === 'switch' && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {field.label}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </span>
                            </div>
                          )}
                          {field.description && (
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                              {field.description}
                            </p>
                          )}
                          {renderField(field)}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            ) : (
              /* Fallback: Render fields without sections */
              <div className="flex flex-wrap -mx-2">
                {template.fields.map((field) => {
                  const widthClass = field.width === 'half' ? 'md:w-1/2' : 
                                   field.width === 'third' ? 'md:w-1/3' : 
                                   field.width === 'quarter' ? 'md:w-1/4' : 'w-full';
                  return (
                <div key={field.id} className={`${widthClass} px-2 mb-4 space-y-2`}>
                  {field.type !== 'checkbox' && field.type !== 'switch' && (
                    <label htmlFor={field.id} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                  )}
                  {field.type === 'switch' && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </span>
                    </div>
                  )}
                  {field.description && (
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      {field.description}
                    </p>
                  )}
                  {renderField(field)}
                </div>
                  );
                })}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.cancel') || 'Mégse'}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 rounded-lg transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (t('common.submitting') || 'Küldés...') : (t('common.submit') || 'Beküldés')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SimpleCustomerFormFiller;
